import React from 'react';

import styled from 'styled-components';

import { DateTimeValue, formatFloating, formatMoment, formatMomentDate } from 'utils/formatters/timezone';

import { LocationAnswerType, UuidableName } from 'utils/api/types';
import { DEFAULT_DATE_FORMAT_WITH_YEAR_AND_HOUR, ProtectorType } from 'core/constants';

import { getRiskBand, isRated } from 'components/RiskMatrix';
import type { RiskMatrixValue } from 'components/RiskMatrix';
import { EntityFieldType, holdsMomentDate, ValueType } from 'utils/api/modules/entities/entities.types';

export const HistoryParagraph = styled.p`
  font-size: 14px;
  margin: 0;
  padding: 0;
`;

const formatValue = ({ protectorType, properties }: EntityFieldType, value: ValueType): JSX.Element => {
  switch (protectorType) {
    case ProtectorType.Date:
      return (
        <b>
          {holdsMomentDate(protectorType, properties?.roles)
            ? formatMomentDate(value as DateTimeValue)
            : formatFloating(value as DateTimeValue, 'date')}
        </b>
      );
    case ProtectorType.DateTime:
      return <b>{formatMoment(value as DateTimeValue, DEFAULT_DATE_FORMAT_WITH_YEAR_AND_HOUR)}</b>;
    case ProtectorType.Time:
      return <b>{formatFloating(value as DateTimeValue, 'time')}</b>;
    case ProtectorType.RiskFactor: {
      // The API returns the rating unresolved, so without this a change reads as the raw object.
      const rating = value as unknown as RiskMatrixValue | null;
      return isRated(rating) ? (
        <b>
          {getRiskBand(rating.severity, rating.likelihood)} ({rating.score})
        </b>
      ) : (
        <b>N/A</b>
      );
    }
    case ProtectorType.Stage:
      // stage can be uuidable name or {key, value: string}
      if (typeof value === 'object' && value && 'key' in value && 'value' in value) {
        return <b>{value.value}</b>;
      }
      return <b>{(value as UuidableName).name}</b>;
    case ProtectorType.Lookup:
    case ProtectorType.Pipeline:
      return <b>{(value as UuidableName).name}</b>;
    case ProtectorType.User:
      return value ? <b>{(value as UuidableName).name}</b> : <b>N/A</b>;
    case ProtectorType.Location:
      return <b>{(value as LocationAnswerType).address}</b>;
    case ProtectorType.SingleChoice:
      return <b>{(value as unknown as { key: string; value: string }[]).map((v) => v.value)}</b>;
    case ProtectorType.MultiChoice:
      return <b>{(value as unknown as { key: string; value: string }[]).map((v) => v.value).join(', ')}</b>;
    default:
      return <b>{value as string}</b>;
  }
};

const getActionVerb = (protectorType: ProtectorType): string => {
  switch (protectorType) {
    case ProtectorType.Stage:
      return 'changed stage to';
    case ProtectorType.Date:
    case ProtectorType.DateTime:
    case ProtectorType.Time:
    case ProtectorType.Lookup:
      return 'set';
    default:
      return 'updated';
  }
};

export const Mapping = (field: EntityFieldType, creator: UuidableName | null, value: ValueType | null): JSX.Element => {
  const { deletedAt, name, protectorType } = field;
  const creatorName = creator?.name || null;
  const actionVerb = getActionVerb(protectorType);
  const formattedValue = value ? formatValue(field, value) : <b>N/A</b>;

  if (protectorType === ProtectorType.Stage) {
    return (
      <HistoryParagraph>
        {creatorName ? <b>{creatorName}</b> : <b>N/A</b>}
        {` ${actionVerb} `}
        {deletedAt ? ` (deleted field)` : ''}
        {formattedValue || <b>N/A</b>}
      </HistoryParagraph>
    );
  }

  if (protectorType === ProtectorType.LongText) {
    return (
      <HistoryParagraph>
        {creatorName ? <b>{creatorName}</b> : <b>N/A</b>}
        {` ${actionVerb} `}
        <b>{name}</b>
        {` to `}
        {value ? `"${value}"` : <b>N/A</b>}
      </HistoryParagraph>
    );
  }

  return (
    <HistoryParagraph>
      {creatorName ? <b>{creatorName}</b> : <b>N/A</b>}
      {` ${actionVerb} `}
      <b>{name}</b>
      {deletedAt ? ` (deleted field)` : ''}
      {` to `}
      {formattedValue || <b>N/A</b>}
    </HistoryParagraph>
  );
};
